// Farm Profile Service - Manages farm profile data and settings
export interface FarmProfile {
  id: string;
  tenantId: string;
  name: string;
  type: 'broiler' | 'layer' | 'mixed' | 'hatchery';
  location: string;
  address: string;
  contactPerson: string;
  phone: string;
  email: string;
  establishedDate: string;
  capacity: number;
  currentBirds: number;
  breeds: string[];
  feedTypes: string[];
  currency: string;
  timezone: string;
  units: {
    weight: 'kg' | 'lbs' | 'g';
    temperature: 'celsius' | 'fahrenheit';
    distance: 'meters' | 'feet';
  };
  settings: {
    notifications: boolean;
    autoBackup: boolean;
    dataRetention: number; // days
    language: string;
  };
  createdAt: string;
  updatedAt: string;
}

class FarmProfileService {
  private profiles: FarmProfile[] = [];

  constructor() {
    this.loadFromStorage();
  }

  // Load data from localStorage
  private loadFromStorage(): void {
    try {
      const profilesData = localStorage.getItem('farmProfiles');
      if (profilesData) {
        this.profiles = JSON.parse(profilesData);
      }
    } catch (error) {
      console.error('Error loading farm profiles from storage:', error);
    }
  }

  // Save data to localStorage
  private saveToStorage(): void {
    try {
      localStorage.setItem('farmProfiles', JSON.stringify(this.profiles));
    } catch (error) {
      console.error('Error saving farm profiles to storage:', error);
    }
  }

  // Get farm profile by tenant ID
  getFarmProfile(tenantId: string): FarmProfile | undefined {
    return this.profiles.find(profile => profile.tenantId === tenantId);
  }

  // Create or update farm profile
  saveFarmProfile(profileData: Omit<FarmProfile, 'id' | 'createdAt' | 'updatedAt'>): FarmProfile {
    const existingIndex = this.profiles.findIndex(p => p.tenantId === profileData.tenantId);
    const now = new Date().toISOString();

    if (existingIndex >= 0) {
      // Update existing profile
      this.profiles[existingIndex] = {
        ...this.profiles[existingIndex],
        ...profileData,
        updatedAt: now,
      };
      this.saveToStorage();
      return this.profiles[existingIndex];
    } else {
      // Create new profile
      const newProfile: FarmProfile = {
        ...profileData,
        id: `farm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: now,
        updatedAt: now,
      };
      this.profiles.push(newProfile);
      this.saveToStorage();
      return newProfile;
    }
  }

  // Get feed types based on farm type
  getFeedTypesForFarmType(farmType?: string): string[] {
    const feedTypes: Record<string, string[]> = {
      broiler: [
        'Starter Feed (0-3 weeks)',
        'Grower Feed (3-6 weeks)',
        'Finisher Feed (6+ weeks)',
        'Pre-starter Feed (0-1 week)'
      ],
      layer: [
        'Chick Starter (0-8 weeks)',
        'Pullet Grower (8-16 weeks)',
        'Layer Feed (16+ weeks)',
        'Layer Premix'
      ],
      mixed: [
        'Starter Feed',
        'Grower Feed',
        'Finisher Feed',
        'Layer Feed',
        'Breeder Feed'
      ],
      hatchery: [
        'Incubation Feed',
        'Chick Starter',
        'Breeder Feed'
      ]
    };

    return feedTypes[farmType || 'broiler'] || feedTypes.broiler;
  }

  // Get available breeds
  getAvailableBreeds(): string[] {
    return [
      'Cobb 500',
      'Ross 308',
      'Hubbard F15',
      'Arbor Acres Plus',
      'Hy-Line Brown',
      'Lohmann Brown',
      'ISA Brown',
      'Rhode Island Red',
      'Leghorn',
      'Sussex',
      'Orpington',
      'Brahma'
    ];
  }

  // Get farm types
  getFarmTypes(): string[] {
    return ['broiler', 'layer', 'mixed', 'hatchery'];
  }

  // Get currencies
  getCurrencies(): string[] {
    return ['UGX', 'USD', 'EUR', 'GBP', 'KES', 'TZS', 'RWF'];
  }

  // Get timezones
  getTimezones(): string[] {
    return [
      'Africa/Kampala',
      'Africa/Nairobi',
      'Africa/Dar_es_Salaam',
      'Africa/Kigali',
      'Africa/Bujumbura',
      'UTC'
    ];
  }

  // Get weight units
  getWeightUnits(): string[] {
    return ['kg', 'lbs', 'g'];
  }

  // Get temperature units
  getTemperatureUnits(): string[] {
    return ['celsius', 'fahrenheit'];
  }

  // Get distance units
  getDistanceUnits(): string[] {
    return ['meters', 'feet'];
  }

  // Get languages
  getLanguages(): string[] {
    return ['en', 'sw', 'fr', 'es'];
  }

  // Delete farm profile
  deleteFarmProfile(tenantId: string): boolean {
    const index = this.profiles.findIndex(profile => profile.tenantId === tenantId);
    if (index === -1) return false;

    this.profiles.splice(index, 1);
    this.saveToStorage();
    return true;
  }

  // Get all farm profiles (for admin purposes)
  getAllFarmProfiles(): FarmProfile[] {
    return [...this.profiles];
  }

  // Get farm statistics
  getFarmStatistics(tenantId: string) {
    const profile = this.getFarmProfile(tenantId);
    if (!profile) {
      return {
        capacity: 0,
        currentBirds: 0,
        utilizationRate: 0,
        establishedDays: 0,
      };
    }

    const establishedDate = new Date(profile.establishedDate);
    const today = new Date();
    const establishedDays = Math.floor((today.getTime() - establishedDate.getTime()) / (1000 * 60 * 60 * 24));
    const utilizationRate = profile.capacity > 0 ? (profile.currentBirds / profile.capacity) * 100 : 0;

    return {
      capacity: profile.capacity,
      currentBirds: profile.currentBirds,
      utilizationRate: Math.round(utilizationRate * 100) / 100,
      establishedDays,
    };
  }

  // Get enabled revenue sources
  getEnabledRevenueSources(): Array<{type: string, name: string, enabled: boolean, estimatedValue: number}> {
    return [
      { type: 'chicken_sales', name: 'Broiler Sales', enabled: true, estimatedValue: 2.5 },
      { type: 'egg_sales', name: 'Layer Eggs', enabled: true, estimatedValue: 0.3 },
      { type: 'chick_sales', name: 'Hatchery Chicks', enabled: true, estimatedValue: 0.8 },
      { type: 'manure_sales', name: 'Manure Sales', enabled: true, estimatedValue: 0.1 },
      { type: 'feather_sales', name: 'Feather Sales', enabled: true, estimatedValue: 0.05 },
      { type: 'other', name: 'Other Revenue', enabled: true, estimatedValue: 0 }
    ];
  }
}

// Export singleton instance
export const farmProfileService = new FarmProfileService();
